import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress, IconButton, Menu, MenuItem, Popover, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AdminPanelSettingsOutlined, ExpandMore, PushPin, SearchOutlined, SettingsOutlined } from '@mui/icons-material';
import { getPortaledOverlayZIndex } from '../utils/portaledPopperProps';
import { isNavPageActive, NavModule, NavPage } from './navigationMenu';
import { getNavTokens, navFocusRing, NavTokens } from './navTokens';

/** Mesma fonte da telemetria; sem a variável, mantém a versão que a sidebar antiga exibia. */
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.2';

export const SIDEBAR_WIDTH_PX = 248;
export const SIDEBAR_COLLAPSED_WIDTH_PX = 64;

const CLOSED_SECTIONS_STORAGE_KEY = 'mestti.sidebar.closedSections';

const readClosedSections = (): Set<string> => {
  try {
    const raw = localStorage.getItem(CLOSED_SECTIONS_STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

interface AppSidebarProps {
  modules: NavModule[];
  modulesLoading?: boolean;
  /** Páginas fixadas como abas favoritas (no topo de cada módulo). */
  pinnedPaths: ReadonlySet<string>;
  pathname: string;
  collapsed: boolean;
  showAdmin: boolean;
  /** Destino da engrenagem: configurações da empresa ou, sem acesso, as da conta. */
  settingsPath: string;
  onNavigate: (path: string) => void;
  onTogglePin: (path: string, label: string) => void;
  onOpenSearch: () => void;
  /** Ausente no celular: o drawer fecha pelo próprio botão. */
  onToggleCollapse?: () => void;
  /** Conteúdo extra no rodapé (ex.: data/hora e configurações no celular). */
  footer?: React.ReactNode;
}

/** Ícone minimalista de painel lateral (o mesmo para recolher e expandir). */
const PanelIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
    <path d="M9.5 4.5v15" />
  </svg>
);

const pageButtonSx = (t: NavTokens, active: boolean, indent: number) => ({
  width: '100%',
  gap: 1,
  height: 32,
  justifyContent: 'flex-start',
  borderRadius: '8px',
  pl: `${indent}px`,
  pr: 1.5,
  fontSize: '0.875rem',
  fontWeight: active ? 600 : 500,
  color: t.ink,
  backgroundColor: active ? t.active : 'transparent',
  transition: 'background-color 120ms ease',
  '&:hover': { backgroundColor: active ? t.active : t.hover },
  ...navFocusRing(t),
});

const moduleLabelSx = (t: NavTokens) => ({
  px: 1.5,
  pb: 0.25,
  fontSize: '0.65625rem',
  lineHeight: '16px',
  fontWeight: 600,
  letterSpacing: '0.6px',
  textTransform: 'uppercase' as const,
  color: t.muted,
});

const PageList: React.FC<{
  module: NavModule;
  pathname: string;
  indent: number;
  pinnedPaths: ReadonlySet<string>;
  onNavigate: (path: string) => void;
  onTogglePin: (path: string, label: string) => void;
}> = ({ module, pathname, indent, pinnedPaths, onNavigate, onTogglePin }) => {
  const t = getNavTokens(useTheme());
  const [menu, setMenu] = useState<{ page: NavPage; x: number; y: number } | null>(null);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
      {module.pages.map((page) => {
        const active = isNavPageActive(pathname, page.path);
        return (
          <ButtonBase
            key={page.path}
            disableRipple
            onClick={() => onNavigate(page.path)}
            onContextMenu={(event) => {
              event.preventDefault();
              setMenu({ page, x: event.clientX, y: event.clientY });
            }}
            aria-current={active ? 'page' : undefined}
            sx={pageButtonSx(t, active, indent)}
          >
            <Box component="span" sx={{ minWidth: 0, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {page.label}
            </Box>
            {pinnedPaths.has(page.path) && (
              <PushPin aria-label="Fixada" sx={{ fontSize: 14, color: t.muted, flex: 'none' }} />
            )}
          </ButtonBase>
        );
      })}
      <Menu
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
        sx={{ zIndex: getPortaledOverlayZIndex }}
      >
        <MenuItem onClick={() => { if (menu) onNavigate(menu.page.path); setMenu(null); }}>Abrir em aba</MenuItem>
        <MenuItem onClick={() => { if (menu) onTogglePin(menu.page.path, menu.page.label); setMenu(null); }}>
          {menu && pinnedPaths.has(menu.page.path) ? 'Desfixar aba' : 'Fixar aba'}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export const AppSidebar: React.FC<AppSidebarProps> = ({
  modules,
  modulesLoading = false,
  pinnedPaths,
  pathname,
  collapsed,
  showAdmin,
  settingsPath,
  onNavigate,
  onTogglePin,
  onOpenSearch,
  onToggleCollapse,
  footer,
}) => {
  const theme = useTheme();
  const t = getNavTokens(theme);
  const listRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const [closedSections, setClosedSections] = useState(readClosedSections);
  // Só o id do módulo: o conteúdo vem sempre da lista atual (permissões podem mudar).
  const [flyout, setFlyout] = useState<{ anchor: HTMLElement; moduleId: string } | null>(null);
  // Expandir/recolher desmonta a âncora: o flyout não pode sobreviver à troca.
  useEffect(() => { setFlyout(null); }, [collapsed]);
  const flyoutModule = flyout ? modules.find((module) => module.id === flyout.moduleId) : undefined;
  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const update = () => setHasMoreBelow(list.scrollTop + list.clientHeight < list.scrollHeight - 1);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(list);
    Array.from(list.children).forEach((child) => observer.observe(child));
    list.addEventListener('scroll', update, { passive: true });
    return () => { observer.disconnect(); list.removeEventListener('scroll', update); };
  }, [collapsed, modules, closedSections]);
  const adminActive = isNavPageActive(pathname, '/admin-panel');
  const settingsActive = isNavPageActive(pathname, '/settings') || isNavPageActive(pathname, '/profile');
  // Categorias recolhíveis; abertas por padrão e lembradas por navegador.
  const toggleSection = (path: string) =>
    setClosedSections((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      try {
        localStorage.setItem(CLOSED_SECTIONS_STORAGE_KEY, JSON.stringify(Array.from(next)));
      } catch {
        // Só conveniência: sem storage, as categorias voltam abertas.
      }
      return next;
    });

  const iconButtonSx = { width: 36, height: 36, color: t.muted, '&:hover': { backgroundColor: t.hover, color: t.ink }, ...navFocusRing(t) };

  if (collapsed) {
    const openFlyout = (event: React.MouseEvent<HTMLElement>, module: NavModule) =>
      setFlyout({ anchor: event.currentTarget, moduleId: module.id });
    const closeFlyout = () => setFlyout(null);

    return (
      <Box component="nav" aria-label="Navegação principal" sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, pb: 1.5, bgcolor: t.chrome }}>
        {/* Mesma linha das abas: área de 38 px na base dos 52 px do topo. */}
        <Box sx={{ height: 52, boxSizing: 'border-box', pt: '14px', display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Expandir menu" placement="right">
            <IconButton aria-label="Expandir menu" onClick={onToggleCollapse} sx={iconButtonSx}><PanelIcon /></IconButton>
          </Tooltip>
        </Box>
        <Tooltip title="Buscar página (Ctrl K)" placement="right">
          <IconButton aria-label="Buscar página" onClick={onOpenSearch} sx={{ ...iconButtonSx, mt: 2, mb: 1.5 }}>
            <SearchOutlined sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        {/* Ícones dos módulos rolam sozinhos em telas baixas; topo e rodapé ficam fixos. */}
        <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        {modulesLoading && <CircularProgress size={18} aria-label="Carregando categorias" />}
        {modules.map((module) => {
          const Icon = module.icon;
          const current = module.pages.some((p) => isNavPageActive(pathname, p.path));
          const open = flyout?.moduleId === module.id;
          return (
            <Tooltip key={module.id} title={open ? '' : module.label} placement="right">
              <ButtonBase
                disableRipple
                aria-label={module.label}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={(event) => openFlyout(event, module)}
                sx={{
                  width: 44,
                  height: 40,
                  borderRadius: '12px',
                  color: current ? t.accent : t.muted,
                  backgroundColor: current || open ? t.active : 'transparent',
                  '&:hover': { backgroundColor: current || open ? t.active : t.hover, color: current ? t.accent : t.ink },
                  ...navFocusRing(t),
                }}
              >
                <Icon sx={{ fontSize: 22 }} />
              </ButtonBase>
            </Tooltip>
          );
        })}
        </Box>
        {showAdmin && (
          <Tooltip title="Painel Admin" placement="right">
            <IconButton
              aria-label="Painel Admin"
              aria-current={adminActive ? 'page' : undefined}
              onClick={() => onNavigate('/admin-panel')}
              sx={{ ...iconButtonSx, width: 44, height: 40, borderRadius: '12px', ...(adminActive && { color: t.accent, backgroundColor: t.active }) }}
            >
              <AdminPanelSettingsOutlined sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Configurações" placement="right">
          <IconButton
            aria-label="Configurações"
            aria-current={settingsActive ? 'page' : undefined}
            onClick={() => onNavigate(settingsPath)}
            sx={{ ...iconButtonSx, width: 44, height: 40, borderRadius: '12px', ...(settingsActive && { color: t.accent, backgroundColor: t.active }) }}
          >
            <SettingsOutlined sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(flyout && flyoutModule)}
          anchorEl={flyout?.anchor}
          onClose={closeFlyout}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{ zIndex: getPortaledOverlayZIndex }}
          slotProps={{
            paper: {
              sx: {
                ml: 1,
                mt: -1,
                width: 232,
                p: 0.75,
                borderRadius: '14px',
                backgroundImage: 'none',
                boxShadow: '0 12px 32px rgba(15,23,42,0.18), 0 2px 6px rgba(15,23,42,0.08)',
              },
            },
          }}
        >
          {flyoutModule && (
            <Box component="nav" aria-label={flyoutModule.label}>
              <Typography component="h2" sx={{ ...moduleLabelSx(t), pt: 0.75, pb: 0.75 }}>
                {flyoutModule.label}
              </Typography>
              <PageList
                module={flyoutModule}
                pathname={pathname}
                indent={12}
                pinnedPaths={pinnedPaths}
                onTogglePin={onTogglePin}
                onNavigate={(path) => {
                  closeFlyout();
                  onNavigate(path);
                }}
              />
            </Box>
          )}
        </Popover>
      </Box>
    );
  }

  return (
    <Box component="nav" aria-label="Navegação principal" sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, px: 1, pb: 1.5, bgcolor: t.chrome }}>
      {/* Mesma linha das abas: área de 38 px na base dos 52 px do topo. */}
      <Box sx={{ height: 52, boxSizing: 'border-box', pt: '14px', flex: 'none', display: 'flex', alignItems: 'center', gap: 1.25, pl: 1 }}>
        <Box component="svg" aria-label="Brasil" role="img" viewBox="0 0 20 14" sx={{ width: 21, height: 15, flex: 'none', transform: 'translateY(-6px)' }}>
          <rect width="20" height="14" fill="#009739" />
          <path d="M10 1.4 18 7 10 12.6 2 7Z" fill="#FFDF00" />
          <circle cx="10" cy="7" r="3.2" fill="#002776" />
          <path d="M7 6.3c2.2-.6 4.4-.2 6.2 1" fill="none" stroke="#fff" strokeWidth=".55" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ width: 'fit-content', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
            <Box component="img" src={`${process.env.PUBLIC_URL}/mestti-wordmark-${theme.palette.mode === 'dark' ? 'dark' : 'light'}.png`} alt="Mestti" sx={{ width: 76, height: 'auto', display: 'block', filter: theme.palette.mode === 'light' ? 'brightness(0)' : undefined }} />
            <Typography component="span" sx={{ fontSize: '0.625rem', lineHeight: 1, whiteSpace: 'nowrap', color: t.muted }}>v{APP_VERSION}</Typography>
          </Box>
        </Box>
        {onToggleCollapse && (
          <Tooltip title="Recolher menu">
            <IconButton aria-label="Recolher menu" onClick={onToggleCollapse} sx={iconButtonSx}><PanelIcon /></IconButton>
          </Tooltip>
        )}
      </Box>

      <ButtonBase
        disableRipple
        onClick={onOpenSearch}
        sx={{
          flex: 'none',
          mt: 2,
          height: 36,
          borderRadius: '10px',
          justifyContent: 'flex-start',
          gap: 1.25,
          px: 1.25,
          fontSize: '0.8125rem',
          color: t.muted,
          backgroundColor: t.hover,
          '&:hover': { color: t.ink },
          ...navFocusRing(t),
        }}
      >
        <SearchOutlined sx={{ fontSize: 18 }} />
        <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>Buscar página</Box>
        <Box component="span" sx={{ fontSize: '0.6875rem' }}>Ctrl K</Box>
      </ButtonBase>

      <Box
        ref={listRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          maskImage: hasMoreBelow ? 'linear-gradient(to bottom, #000 calc(100% - 24px), transparent 100%)' : 'none',
          WebkitMaskImage: hasMoreBelow ? 'linear-gradient(to bottom, #000 calc(100% - 24px), transparent 100%)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          pt: 3,
          pb: 1,
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          '&:hover': { scrollbarColor: `${t.hover} transparent` },
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent', borderRadius: 3 },
          '&:hover::-webkit-scrollbar-thumb': { backgroundColor: t.hover },
        }}
      >
        {modulesLoading && <Box sx={{ px: 2 }}><CircularProgress size={18} aria-label="Carregando categorias" /></Box>}
        {modules.map((module) => {
          const open = !closedSections.has(module.id);
          const current = module.pages.some((p) => isNavPageActive(pathname, p.path));
          return (
            <Box component="section" key={module.id} aria-label={module.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <ButtonBase
                disableRipple
                aria-expanded={open}
                onClick={() => toggleSection(module.id)}
                sx={{
                  ...moduleLabelSx(t),
                  height: 26,
                  pb: 0,
                  borderRadius: '6px',
                  justifyContent: 'flex-start',
                  gap: 1,
                  color: current && !open ? t.ink : t.muted,
                  '&:hover': { color: t.ink },
                  '&:hover .section-chevron': { opacity: 1 },
                  ...navFocusRing(t),
                }}
              >
                <module.icon sx={{ fontSize: 16 }} aria-hidden="true" />
                <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>{module.label}</Box>
                <ExpandMore
                  className="section-chevron"
                  aria-hidden="true"
                  sx={{ fontSize: 16, opacity: open ? 0 : 1, transform: open ? 'none' : 'rotate(-90deg)' }}
                />
              </ButtonBase>
              {open && (
                <PageList
                  module={module}
                  pathname={pathname}
                  indent={36}
                  pinnedPaths={pinnedPaths}
                  onTogglePin={onTogglePin}
                  onNavigate={onNavigate}
                />
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: '1px', mt: 1 }}>
        {showAdmin && (
          <ButtonBase
            disableRipple
            onClick={() => onNavigate('/admin-panel')}
            aria-current={adminActive ? 'page' : undefined}
            sx={{ ...pageButtonSx(t, adminActive, 12), height: 36, gap: 0.75 }}
          >
            <AdminPanelSettingsOutlined sx={{ fontSize: 18, color: adminActive ? t.accent : t.muted }} />
            Painel Admin
          </ButtonBase>
        )}
        <ButtonBase
          disableRipple
          onClick={() => onNavigate(settingsPath)}
          aria-current={settingsActive ? 'page' : undefined}
          sx={{ ...pageButtonSx(t, settingsActive, 12), height: 36, gap: 0.75 }}
        >
          <SettingsOutlined sx={{ fontSize: 18, color: settingsActive ? t.accent : t.muted }} />
          Configurações
        </ButtonBase>
      </Box>
      {footer}
    </Box>
  );
};
